import React, { useState, useCallback } from 'react';
import {
    View, Text, TextInput, TouchableOpacity, StyleSheet,
} from 'react-native';
import { InputMode } from '@eduwrite/shared';
import { InputModeSwitcher } from './InputModeSwitcher';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { HoldToSpeakButton } from './HoldToSpeakButton';
import { stopTextToSpeech } from './AudioPromptButton';
import { HandwritingCanvasPanel, NumpadInputPanel, TileInputPanel } from './input';

interface MultiModalInputAreaProps {
    allowedModes: InputMode[];
    defaultMode: InputMode;
    onChange: (value: string) => void;
    onSubmit?: (value: string, modeUsed: InputMode, meta?: { voiceTranscript?: string; voiceRecordingUrl?: string }) => void;
    onModeChange?: (mode: InputMode) => void;
    placeholder?: string;
    keyboardType?: 'default' | 'numeric';
    wordBank?: string[];
    choices?: string[];
}

/**
 * MultiModalInputArea — the student-facing input hub.
 *
 * Renders the appropriate input panel (canvas, keyboard, speech, numpad,
 * word_tap, choice_tap) based on the active mode. Shows the mode switcher
 * when the teacher has allowed more than one mode.
 */
export const MultiModalInputArea: React.FC<MultiModalInputAreaProps> = ({
    allowedModes,
    defaultMode,
    onChange,
    onSubmit,
    onModeChange,
    placeholder = 'Enter your answer...',
    keyboardType = 'default',
    wordBank,
    choices,
}) => {
    const [activeMode, setActiveMode] = useState<InputMode>(defaultMode);
    const [typedValue, setTypedValue] = useState('');
    const [_selectedWords, setSelectedWords] = useState<string[]>([]);
    const [selectedChoice, setSelectedChoice] = useState<string | null>(null);

    const { state: voiceState, transcript, error: voiceError, startListening, stopListening, reset: resetVoice } = useVoiceInput({
        onResult: onChange,
    });

    const handleModeChange = useCallback((mode: InputMode) => {
        setActiveMode(mode);
        onModeChange?.(mode);
        resetVoice();
        setSelectedWords([]);
        setSelectedChoice(null);
        setTypedValue('');
        onChange('');
    }, [resetVoice, onModeChange, onChange]);

    const handleTypedChange = (text: string) => {
        setTypedValue(text);
        onChange(text);
    };

    const handleTypedSubmit = () => {
        if (typedValue.trim()) {
            onSubmit?.(typedValue.trim(), 'keyboard');
        }
    };

    const handleChoiceTap = (choice: string) => {
        setSelectedChoice(choice);
        onChange(choice);
        onSubmit?.(choice, 'choice_tap');
    };


    return (
        <View style={styles.container}>
            <InputModeSwitcher
                allowedModes={allowedModes}
                activeMode={activeMode}
                onSelectMode={handleModeChange}
            />

            {/* --- KEYBOARD PANEL --- */}
            {activeMode === 'keyboard' && (
                <View style={styles.panel}>
                    <TextInput
                        style={styles.textInput}
                        placeholder={placeholder}
                        placeholderTextColor="#64748b"
                        value={typedValue}
                        onChangeText={handleTypedChange}
                        keyboardType={keyboardType}
                        autoCorrect={false}
                        multiline
                        numberOfLines={3}
                        accessibilityLabel="Answer input"
                        accessibilityHint="Type your answer here"
                    />
                    <TouchableOpacity
                        style={styles.submitBtn}
                        onPress={handleTypedSubmit}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel="Submit"
                        accessibilityHint="Double tap to submit your answer"
                    >
                        <Text style={styles.submitBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Submit</Text>
                    </TouchableOpacity>
                </View>
            )}

            {/* --- SPEECH PANEL --- */}
            {activeMode === 'speech' && (
                <View style={styles.panel}>
                    {(voiceState === 'idle' || voiceState === 'listening' || voiceState === 'processing') && (
                        <HoldToSpeakButton
                            state={voiceState}
                            onStart={() => { stopTextToSpeech(); void startListening(); }}
                            onStop={() => { void stopListening(); }}
                        />
                    )}
                    {voiceState === 'done' && transcript && (
                        <View style={styles.voiceResult}>
                            <Text style={styles.voiceResultText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{transcript}</Text>
                            <TouchableOpacity
                                style={styles.submitBtn}
                                onPress={() => {
                                    onSubmit?.(transcript, 'speech', { voiceTranscript: transcript });
                                    resetVoice();
                                }}
                                activeOpacity={0.7}
                                accessibilityRole="button"
                                accessibilityLabel="Use This"
                                accessibilityHint="Double tap to use this transcript as your answer"
                            >
                                <Text style={styles.submitBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Use This</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.retryBtn}
                                onPress={resetVoice}
                                activeOpacity={0.7}
                                accessibilityRole="button"
                                accessibilityLabel="Try Again"
                                accessibilityHint="Double tap to record your voice again"
                            >
                                <Text style={styles.retryBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Try Again</Text>
                            </TouchableOpacity>
                        </View>
                    )}
                    {voiceState === 'error' && (
                        <View style={styles.voiceResult}>
                            <Text style={styles.voiceErrorText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{voiceError || 'Could not hear you. Try again!'}</Text>
                            <TouchableOpacity
                                style={styles.retryBtn}
                                onPress={resetVoice}
                                activeOpacity={0.7}
                                accessibilityRole="button"
                                accessibilityLabel="Try Again"
                                accessibilityHint="Double tap to try recording again"
                            >
                                <Text style={styles.retryBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Try Again</Text>
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
            )}

            {/* --- NUMPAD PANEL --- */}
            {activeMode === 'numpad' && (
                <NumpadInputPanel
                    placeholder={placeholder}
                    onChange={(val) => {
                        setTypedValue(val);
                        onChange(val);
                    }}
                    onSubmit={(val) => onSubmit?.(val, 'numpad')}
                />
            )}

            {/* --- WORD TAP PANEL --- */}
            {activeMode === 'word_tap' && wordBank && (
                <TileInputPanel
                    wordBank={wordBank}
                    placeholder={placeholder}
                    onChange={(val) => {
                        setSelectedWords(val ? val.split(' ') : []);
                        onChange(val);
                    }}
                    onSubmit={(val) => onSubmit?.(val, 'word_tap')}
                />
            )}

            {/* --- CHOICE TAP PANEL --- */}
            {activeMode === 'choice_tap' && choices && (
                <View style={styles.panel}>
                    <View style={styles.choiceGrid}>
                        {choices.map((choice) => {
                            const selected = choice === selectedChoice;
                            return (
                                <TouchableOpacity
                                    key={choice}
                                    style={[styles.choiceBtn, selected && styles.choiceBtnSelected]}
                                    onPress={() => handleChoiceTap(choice)}
                                    activeOpacity={0.6}
                                    accessibilityRole="button"
                                    accessibilityLabel={choice}
                                    accessibilityHint="Double tap to select this answer"
                                    accessibilityState={{ selected }}
                                >
                                    <Text style={[styles.choiceBtnText, selected && styles.choiceBtnTextSelected]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                        {choice}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                </View>
            )}

            {/* --- CANVAS PANEL --- */}
            {activeMode === 'canvas' && <HandwritingCanvasPanel />}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        // flexGrow + flexBasis 'auto' (not flex:1): fills bounded parents but
        // sizes to content inside indefinite-height containers, where a
        // zero-basis flex would collapse to 0 height under Yoga.
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
    },
    panel: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        padding: 16,
        justifyContent: 'center',
    },
    textInput: {
        backgroundColor: '#F8FAFC',
        borderRadius: 14,
        paddingHorizontal: 16,
        paddingVertical: 14,
        color: '#0F172A',
        fontSize: 18,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        textAlignVertical: 'top',
        minHeight: 100,
    },
    submitBtn: {
        marginTop: 16,
        backgroundColor: '#22c55e',
        borderRadius: 14,
        paddingVertical: 16,
        alignItems: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    submitBtnText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: '800',
    },
    voiceBtn: {
        alignSelf: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        width: 140,
        height: 140,
        borderRadius: 70,
        backgroundColor: '#EEF2FF',
        borderWidth: 3,
        borderColor: '#818CF8',
    },
    voiceBtnActive: {
        backgroundColor: '#FEE2E2',
        borderColor: '#EF4444',
    },
    voiceBtnIcon: {
        fontSize: 40,
        marginBottom: 6,
    },
    voiceBtnText: {
        fontSize: 15,
        fontWeight: '800',
        color: '#4338CA',
    },
    voiceResult: {
        alignItems: 'center',
        padding: 16,
    },
    voiceResultText: {
        fontSize: 20,
        fontWeight: '700',
        color: '#0F172A',
        textAlign: 'center',
        marginBottom: 16,
        lineHeight: 28,
    },
    voiceErrorText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#DC2626',
        textAlign: 'center',
        marginBottom: 16,
    },
    retryBtn: {
        marginTop: 12,
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 12,
        backgroundColor: '#F1F5F9',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    retryBtnText: {
        color: '#475569',
        fontSize: 15,
        fontWeight: '700',
    },
    choiceGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'center',
    },
    choiceBtn: {
        width: '46%',
        minHeight: 64,
        backgroundColor: '#EEF2FF',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: '#C7D2FE',
        padding: 12,
    },
    choiceBtnSelected: {
        backgroundColor: '#4338CA',
        borderColor: '#3730A3',
    },
    choiceBtnText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#3730A3',
        textAlign: 'center',
    },
    choiceBtnTextSelected: {
        color: '#FFFFFF',
    },
});
